import { Edit, Delete } from "@mui/icons-material";
import { FoodsIcon, MRTGrid } from "@foodpilot/foods";
import { IconButton } from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import { Fragment, useState } from "react";
import { useSupplierListContext } from "../SupplierListContext";
import { useTranslation } from "react-i18next";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import ActionsPopover from "src/components/ActionsPopover/ActionsPopover";
import { allCompanyKey } from "src/api/client-api/company/useGetAllCompany";
import { useNavigate } from "react-router-dom";
import { URLHandler } from "src/services/URLHandler";
import { useDeleteCompany } from "src/api/client-api/company/useDeleteCompany";
import { useQueryClient } from "react-query";
import { useSnackbar } from "src/utils/useSnackbar";

const SuppliersGrid = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const deleteSupplier = useDeleteCompany();
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();
    const {
        mrt_columns,
        suppliers = [],
        isLoading,
        delayedSearchSupplier,
        totalItems,
        supplierPage,
        setSupplierPage,
        sorting,
        setSorting,
    } = useSupplierListContext();
    const [selectedRowItemId, setSelectedRowItemId] = useState<number | null>(null);
    const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);

    const selectedSupplier = suppliers?.find((sup) => sup.id === selectedRowItemId);

    const menuItems: MenuItemType[] = [
        ...(selectedSupplier?.canView ?
            [
                {
                    icon: <FoodsIcon icon="scoresIndicators" size={3} />,
                    label: t("Scores & indicateurs clés"),
                    onClick: () => {},
                    isDisabled: true,
                },
                {
                    icon: <FoodsIcon icon="questionnaires" size={3} />,
                    label: t("Questionnaire"),
                    onClick: () => {},
                    isDisabled: true,
                },
                {
                    icon: <FoodsIcon icon="informations" size={3} />,
                    label: t("Informations"),
                    onClick: () => {},
                    isDisabled: true,
                },
            ]
        :   []),
        ...(selectedSupplier?.canModify ?
            [
                {
                    icon: <Edit />,
                    label: t("Modifier"),
                    onClick: () => {
                        if (selectedSupplier && selectedRowItemId)
                            navigate(URLHandler.supplier.edit(selectedSupplier.id, selectedRowItemId, 0));
                    },
                },
            ]
        :   []),
        ...(selectedSupplier?.canDelete ?
            [
                {
                    icon: <Delete />,
                    label: t("Supprimer"),
                    onClick: () => {
                        if (selectedRowItemId)
                            deleteSupplier.mutate(selectedRowItemId, {
                                onSuccess: () => {
                                    queryClient.invalidateQueries({ queryKey: allCompanyKey });
                                    setRowMenuAnchor(null);
                                },
                                onError: () => {
                                    snackbar.setSnackbarProps({
                                        message: t(
                                            "La suppression du fournisseur n'est pas possible car un utilisateur lui est rattaché.",
                                        ),
                                        type: "error",
                                    });
                                },
                            });
                    },
                },
            ]
        :   []),
    ];

    return (
        <Fragment>
            <MRTGrid
                columns={mrt_columns.mrt_columns}
                data={suppliers}
                globalFilterFn="contains"
                enableRowActions={true}
                enableRowSelection={false}
                enablePagination={true}
                enableSelectAll={false}
                enableColumnPinning={true}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        size: 80,
                        grow: false,
                    },
                }}
                renderRowActions={(actionProps) => {
                    const { row } = actionProps;
                    const ingredientId = row.original.id;

                    return (
                        <IconButton
                            onClick={(e) => {
                                setSelectedRowItemId(ingredientId);
                                setRowMenuAnchor(e.currentTarget);
                            }}
                            sx={{
                                transform: "translateX(-4px)",
                            }}
                        >
                            <MoreHoriz />
                        </IconButton>
                    );
                }}
                state={{
                    isLoading: isLoading,
                    columnVisibility: mrt_columns.columnVisibility,
                    columnOrder: mrt_columns.columnOrder,
                    globalFilter: delayedSearchSupplier,
                    pagination: supplierPage,
                    columnPinning: {
                        left: [mrt_columns.columnOrder[0]],
                        right: ["mrt-row-actions"],
                    },
                    columnSizing: mrt_columns.columnSizing,
                    sorting: sorting,
                }}
                onColumnSizingChange={mrt_columns.setColumnSizing}
                // Server-side pagination
                rowCount={totalItems}
                manualPagination={true}
                onPaginationChange={setSupplierPage}
                localization={{
                    actions: "",
                }}
                manualSorting={true}
                onSortingChange={setSorting}
            />
            <ActionsPopover menuItems={menuItems} anchor={rowMenuAnchor} closeWindow={() => setRowMenuAnchor(null)} />
        </Fragment>
    );
};

export default SuppliersGrid;
